import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { ArrowLeft, Users, Edit2, Save, MessageSquare, AlertTriangle, Search } from 'lucide-react';
import { toast } from 'sonner';

export default function TrainerBatchDetailPage() {
  const { batchId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editComment, setEditComment] = useState(false);
  const [comment, setComment] = useState('');
  const [whatsappLink, setWhatsappLink] = useState('');
  const [remarkDialog, setRemarkDialog] = useState(null);
  const [remarkForm, setRemarkForm] = useState({ attendance: '', ready_for_placement: false, sector: '', pdp: '', mock_interview: '', mock_interview_link: '' });
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(() => {
    setLoading(true);
    api.get(`/trainer/batches/${batchId}/students`)
      .then(r => {
        setData(r.data);
        setComment(r.data.comment || '');
        setWhatsappLink(r.data.whatsapp_link || '');
      })
      .catch(() => toast.error('Failed to load batch'))
      .finally(() => setLoading(false));
  }, [batchId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSaveComment = async () => {
    try {
      await api.put(`/trainer/batches/${batchId}/comment`, { comment, whatsapp_link: whatsappLink });
      toast.success('Comment saved');
      setEditComment(false);
      fetchData();
    } catch { toast.error('Failed to save comment'); }
  };

  const openRemarkDialog = (student) => {
    setRemarkDialog(student);
    setRemarkForm({
      attendance: student.attendance || '',
      ready_for_placement: student.ready_for_placement || false,
      sector: student.sector || '',
      pdp: student.pdp || '',
      mock_interview: student.mock_interview || '',
      mock_interview_link: student.mock_interview_link || '',
    });
  };

  const handleSaveRemark = async () => {
    if (!remarkDialog) return;
    setSaving(true);
    try {
      await api.put(`/trainer/batches/${batchId}/students/${remarkDialog.student_id}/remarks`, remarkForm);
      toast.success('Remarks updated');
      setRemarkDialog(null);
      fetchData();
    } catch { toast.error('Failed'); }
    setSaving(false);
  };

  if (loading) {
    return <div className="h-64 flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-500" /></div>;
  }

  if (!data) return <p className="text-sm text-slate-400 py-12 text-center">Batch not found or access denied</p>;

  const batch = data.batch;
  const filteredStudents = search
    ? data.students.filter(s => (s.name || '').toLowerCase().includes(search.toLowerCase()) || (s.enrollment_no || '').toLowerCase().includes(search.toLowerCase()) || (s.phone || '').includes(search))
    : data.students;

  return (
    <div className="space-y-6" data-testid="trainer-batch-detail">
      <Button variant="ghost" size="sm" className="text-xs text-slate-500 -ml-2" onClick={() => navigate('/trainer/dashboard')}>
        <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Back to My Batches
      </Button>

      {/* Batch Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-bold text-slate-900">Batch #{batch.batch_number}</h1>
            <Badge variant="outline" className={`text-[10px] px-2 py-0.5 ${
              batch.status === 'Running' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
              batch.status === 'Completed' ? 'bg-slate-100 text-slate-600' :
              batch.status === 'On Hold' ? 'bg-amber-50 text-amber-700' :
              'bg-blue-50 text-blue-700'
            }`}>{batch.status}</Badge>
          </div>
          <div className="flex items-center gap-4 mt-1 text-xs text-slate-500">
            <span>{batch.course_name}</span>
            <span>{batch.centre_name}</span>
            <span>{batch.batch_type}</span>
            <span>{batch.start_date} {batch.end_date ? `- ${batch.end_date}` : ''}</span>
            {batch.timing && <span>{batch.timing}</span>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-xs"><Users className="w-3 h-3 mr-1" />{data.total_students} Students</Badge>
        </div>
      </div>

      {/* Batch Comment */}
      <Card className="border p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <MessageSquare className="w-4 h-4 text-violet-500" />
            <p className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Batch Notes</p>
          </div>
          <Button variant="ghost" size="sm" className="h-7 text-xs text-violet-600" onClick={() => setEditComment(!editComment)} data-testid="toggle-comment-edit">
            {editComment ? 'Cancel' : <><Edit2 className="w-3 h-3 mr-1" /> Edit</>}
          </Button>
        </div>
        {editComment ? (
          <div className="space-y-2">
            <Textarea value={comment} onChange={e => setComment(e.target.value)} className="text-xs min-h-[60px]" placeholder="Add batch notes/comments..." data-testid="batch-comment-input" />
            <div className="flex items-center gap-2">
              <Input value={whatsappLink} onChange={e => setWhatsappLink(e.target.value)} placeholder="WhatsApp group link (optional)" className="h-8 text-xs flex-1" data-testid="whatsapp-link-input" />
              <Button size="sm" className="h-8 bg-violet-600 hover:bg-violet-700 text-xs" onClick={handleSaveComment} data-testid="save-comment-btn">
                <Save className="w-3 h-3 mr-1" /> Save
              </Button>
            </div>
          </div>
        ) : (
          <div>
            {comment ? <p className="text-xs text-slate-600 whitespace-pre-wrap">{comment}</p> : <p className="text-xs text-slate-300">No notes yet</p>}
            {whatsappLink && (
              <a href={whatsappLink} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 mt-2 text-[10px] text-emerald-600 hover:underline">
                WhatsApp Group
              </a>
            )}
          </div>
        )}
      </Card>

      {/* Student List */}
      <Card className="border p-0">
        <div className="flex items-center gap-3 p-4 border-b">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input placeholder="Search students..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 h-9 text-xs" data-testid="trainer-student-search" />
          </div>
          <span className="text-xs text-slate-400">{filteredStudents.length} student{filteredStudents.length !== 1 ? 's' : ''}</span>
        </div>

        {filteredStudents.length === 0 ? (
          <div className="text-center py-12"><p className="text-xs text-slate-400">No students found</p></div>
        ) : (
          <Table>
            <TableHeader><TableRow>
              <TableHead className="text-[10px] uppercase">Enr #</TableHead>
              <TableHead className="text-[10px] uppercase">Name</TableHead>
              <TableHead className="text-[10px] uppercase">Phone</TableHead>
              <TableHead className="text-[10px] uppercase">Email</TableHead>
              <TableHead className="text-[10px] uppercase">Type</TableHead>
              <TableHead className="text-[10px] uppercase">Dues</TableHead>
              <TableHead className="text-[10px] uppercase">Attendance</TableHead>
              <TableHead className="text-[10px] uppercase">Placement</TableHead>
              <TableHead className="text-[10px] uppercase text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {filteredStudents.map(s => (
                <TableRow key={s.student_id} data-testid={`trainer-student-row-${s.student_id}`}>
                  <TableCell className="text-xs font-medium text-violet-600">{s.enrollment_no || '-'}</TableCell>
                  <TableCell className="text-sm font-medium">{s.name}</TableCell>
                  <TableCell className="text-xs text-slate-500">{s.phone || '-'}</TableCell>
                  <TableCell className="text-xs text-slate-500 max-w-[150px] truncate">{s.email || '-'}</TableCell>
                  <TableCell>
                    {s.enrollment_type !== 'regular' ? (
                      <Badge variant="outline" className="text-[9px] bg-purple-50 text-purple-600 border-purple-200">{s.enrollment_type === 'upgrade' ? 'Upg' : 'Rev'}</Badge>
                    ) : (
                      <span className="text-[9px] text-slate-400">Regular</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {s.dues_status === 'overdue' ? (
                      <Badge variant="outline" className="text-[9px] bg-red-50 text-red-600 border-red-200"><AlertTriangle className="w-2.5 h-2.5 mr-0.5" />Overdue</Badge>
                    ) : s.dues_status === 'partially_paid' ? (
                      <Badge variant="outline" className="text-[9px] bg-amber-50 text-amber-600 border-amber-200">Partial</Badge>
                    ) : (
                      <span className="text-[9px] text-slate-300">-</span>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-slate-500">{s.attendance || '-'}</TableCell>
                  <TableCell>
                    {s.ready_for_placement ? (
                      <Badge variant="outline" className="text-[9px] bg-emerald-50 text-emerald-600">Ready</Badge>
                    ) : (
                      <span className="text-[9px] text-slate-300">-</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" className="h-7 text-[10px] text-violet-600" onClick={() => openRemarkDialog(s)} data-testid={`edit-remark-${s.student_id}`}>
                      <Edit2 className="w-3 h-3 mr-1" /> Remarks
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      {/* Remarks Dialog */}
      <Dialog open={!!remarkDialog} onOpenChange={() => setRemarkDialog(null)}>
        <DialogContent className="max-w-md" data-testid="remark-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">Student Remarks — {remarkDialog?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs">Attendance</Label>
              <Select value={remarkForm.attendance || 'none'} onValueChange={v => setRemarkForm(p => ({ ...p, attendance: v === 'none' ? '' : v }))}>
                <SelectTrigger className="h-8 text-xs" data-testid="remark-attendance"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Not Set</SelectItem>
                  <SelectItem value="Regular">Regular</SelectItem>
                  <SelectItem value="Irregular">Irregular</SelectItem>
                  <SelectItem value="Dropped">Dropped</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox checked={remarkForm.ready_for_placement} onCheckedChange={v => setRemarkForm(p => ({ ...p, ready_for_placement: !!v }))} data-testid="remark-placement" />
              <Label className="text-xs">Ready for Placement</Label>
            </div>
            <div>
              <Label className="text-xs">Sector / Industry</Label>
              <Input value={remarkForm.sector} onChange={e => setRemarkForm(p => ({ ...p, sector: e.target.value }))} className="h-8 text-xs" placeholder="e.g. IT, Finance..." data-testid="remark-sector" />
            </div>
            <div>
              <Label className="text-xs">PDP Status</Label>
              <Select value={remarkForm.pdp || 'none'} onValueChange={v => setRemarkForm(p => ({ ...p, pdp: v === 'none' ? '' : v }))}>
                <SelectTrigger className="h-8 text-xs" data-testid="remark-pdp"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Not Set</SelectItem>
                  <SelectItem value="Completed">Completed</SelectItem>
                  <SelectItem value="In Progress">In Progress</SelectItem>
                  <SelectItem value="Not Started">Not Started</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Mock Interview</Label>
              <Select value={remarkForm.mock_interview || 'none'} onValueChange={v => setRemarkForm(p => ({ ...p, mock_interview: v === 'none' ? '' : v }))}>
                <SelectTrigger className="h-8 text-xs" data-testid="remark-mock"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Not Set</SelectItem>
                  <SelectItem value="Completed">Completed</SelectItem>
                  <SelectItem value="Scheduled">Scheduled</SelectItem>
                  <SelectItem value="Not Done">Not Done</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs">Mock Interview Link
              <span className="text-[10px] text-slate-400 font-normal ml-1">— video / transcript URL (shared drive, YouTube, etc.)</span>
            </Label>
            <Input type="url" value={remarkForm.mock_interview_link} placeholder="https://..."
              onChange={e => setRemarkForm(p => ({ ...p, mock_interview_link: e.target.value }))}
              data-testid="remark-mock-link" className="h-8 text-xs mt-1" />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" className="text-xs" onClick={() => setRemarkDialog(null)}>Cancel</Button>
            <Button size="sm" className="bg-violet-600 hover:bg-violet-700 text-xs" onClick={handleSaveRemark} disabled={saving} data-testid="save-remark-btn">
              {saving ? 'Saving...' : 'Save Remarks'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
